import { postJson, useAuthentication, useQueryParam, withLoginSubmit } from '@psyomics/ui-common';
import { CodeCaptureForm, LoginForm } from '@psyomics/ui-components';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { getConfig } from '../config';
import { useOrganisationReferrerEmailDomainWhitelist } from '../features/self-hosted-register/hooks/use-organisation-referrer-email-domain-whitelist';

import { REFERRAL_REDIRECT_WHITELIST } from './redirect-whitelists';

type Challenge = {
  session: string;
  username: string;
};

const MfaChallengeStep = ({
  apiUrl,
  challenge,
  getAuthSessionContext,
  onComplete,
  onRetry,
  organisation,
  postLogin,
  redirect,
}: {
  readonly challenge: Challenge;
  readonly apiUrl: string;
  readonly getAuthSessionContext: () => { orgCode?: string };
  readonly onComplete: () => void;
  readonly onRetry: () => void;
  readonly organisation?: string;
  readonly postLogin: ReturnType<typeof useAuthentication>['postLogin'];
  readonly redirect?: string;
}) => {
  const { t } = useTranslation();

  const handleSubmit = async (code: string) => {
    try {
      const response = await postJson(`${apiUrl}/auth/mfa/login`, {
        code,
        context: getAuthSessionContext(),
        ...(organisation && { organisation }),
        session: challenge.session,
        username: challenge.username,
      });

      const payload = await response.json().catch(() => null);
      if (!payload?.success) {
        throw new Error('mfa login failed');
      }

      postLogin(payload, redirect, REFERRAL_REDIRECT_WHITELIST);
      onComplete();
    } catch {
      throw new Error('mfa login failed');
    }
  };

  return (
    <CodeCaptureForm
      autoComplete="one-time-code"
      buttonLabel={t('auth.login.mfa.submit')}
      description={t('auth.common.mfa.codeDescription')}
      errorRequired={t('auth.common.errors.confirmationCodeMissing')}
      inputLabel={t('auth.login.mfa.codeInputLabel')}
      inputMode="numeric"
      onRetry={onRetry}
      onSubmit={handleSubmit}
      title={t('auth.login.mfa.title')}
    />
  );
};

const LoginWithSubmit = withLoginSubmit(LoginForm, {
  redirectWhitelist: REFERRAL_REDIRECT_WHITELIST,
});

const SelfHostedLogin = ({ forgotPasswordUrl }: { readonly forgotPasswordUrl: string }) => {
  const navigate = useNavigate();
  const { referralApiUrl } = getConfig();
  const orgCode = useQueryParam('orgCode') ?? undefined;
  const redirect = useQueryParam('redirect') ?? undefined;
  const { organisation } = useParams();
  const { postLogin } = useAuthentication();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [allowedEmailDomains] = useOrganisationReferrerEmailDomainWhitelist(orgCode);

  const getAuthSessionContext = useCallback(
    () => ({
      orgCode,
    }),
    [orgCode]
  );

  if (challenge) {
    return (
      <div className="form-container">
        <MfaChallengeStep
          apiUrl={referralApiUrl}
          challenge={challenge}
          getAuthSessionContext={getAuthSessionContext}
          onComplete={() => setChallenge(null)}
          onRetry={() => navigate('/')}
          organisation={organisation}
          postLogin={postLogin}
          redirect={redirect}
        />
      </div>
    );
  }

  return (
    <div className="w-full flex justify-center mt-8">
      <div className="w-full max-w-[327px] mx-[24px]">
        <LoginWithSubmit
          allowedEmailDomains={allowedEmailDomains}
          apiUrl={referralApiUrl}
          forgotPasswordUrl={forgotPasswordUrl}
          getAuthSessionContext={getAuthSessionContext}
          onMfaChallenge={({ session, username }: { session: string; username: string }) =>
            setChallenge({ session, username })
          }
        />
      </div>
    </div>
  );
};

export { SelfHostedLogin };
